// @flow

import React, { useEffect, useState } from 'react';

import { createInviteDialogEvent, sendAnalytics } from '../../../../analytics';
import { getInviteURL } from '../../../../base/connection';
import { translate, i18next } from '../../../../base/i18n';
import { IconInfo } from '../../../../base/icons';
import { JitsiRecordingConstants } from '../../../../base/lib-jitsi-meet';
import { connect } from '../../../../base/redux';
import { getActiveSession } from '../../../../recording';
import { updateDialInNumbers } from '../../../actions';
import {
    _getDefaultPhoneNumber,
    isAddPeopleEnabled,
    isDialOutEnabled,
    sharingFeatures,
    isSharingEnabled,
    _decodeRoomURI,
    shouldDisplayDialIn,
    getDialInfoPageURL
} from '../../../functions';
import RPC_CopyButton from '../../../../base/buttons/RPC_CopyButton';
import { getDecodedURI } from '../../../../base/util';

import { getLocalParticipant } from '../../../../base/participants';

declare var interfaceConfig: Object;

function MeetingContent({
    _dialIn,
    t,
    _meeting, _password,
    updateNumbers, _invitationText }: Props) {

    const [_is_more, setMore] = useState(0);
    /**
     * Updates the dial-in numbers.
     */
    useEffect(() => {
        if (!_dialIn.numbers) {
            updateNumbers();
        }
    }, []);

    /**
     * Sends analytics events when the dialog opens/closes.
     *
     * @returns {void}
     */
    useEffect(() => {
        sendAnalytics(createInviteDialogEvent(
            'invite.dialog.opened', 'dialog'));

        return () => {
            sendAnalytics(createInviteDialogEvent(
                'invite.dialog.closed', 'dialog'));
        };
    }, []);

    const inviteSubject = t('addPeople.inviteMoreMailSubject', {
        appName: interfaceConfig.APP_NAME
    });

    return (
        <>
        <div className = 'invite-more-header'>
            <IconInfo />
            Details
        </div>
        <div className = 'invite-more-dialog' style={{ width: '300px' }}>
            <h3>{ _meeting.name }</h3>
            <h4>Joining Info</h4>
            <p className="url">{ window.location.href }</p>
            { (_meeting.password || _password) && <p>Use Meeting Password : { _meeting.password || _password }</p>}
            <p>Dial In : (US) +1-818-699-0758</p>
            <p>PIN : { _meeting.pin.toString().match(/.{1,3}/g).join(' ') }</p><br/>
            { _is_more ?
                <><a onClick={ () => { setMore(0); } }>Less Numbers</a><br/>
                <small>(US) +1-818-699-0758</small><br/>
                <small>(UK) +44-808-168-6041</small><br/>
                <small>(Canada) +1-647-697-4768</small></>
                :
                <a onClick={ () => { setMore(1); } }>More Numbers</a>
            }
            <br/><br/>
            <RPC_CopyButton
                className = 'invite-more-dialog-conference-url'
                displayedText = { 'Copy Joining Info' }
                textOnCopySuccess = { t('addPeople.linkCopied') }
                textOnHover = { t('addPeople.copyLink') }
                textToCopy = { _invitationText } />
        </div>
        </>
    );
}

/**
 * Maps (parts of) the Redux state to the associated props for the
 * {@code MeetingContent} component.
 *
 * @param {Object} state - The Redux state.
 * @param {Object} ownProps - The properties explicitly passed to the component.
 * @private
 * @returns {Props}
 */
function mapStateToProps(state, ownProps) {
    const currentLiveStreamingSession
        = getActiveSession(state, JitsiRecordingConstants.mode.STREAM);
    const { iAmRecorder } = state['features/base/config'];
    const addPeopleEnabled = isAddPeopleEnabled(state);
    const dialOutEnabled = isDialOutEnabled(state);
    const hideInviteContacts = iAmRecorder || (!addPeopleEnabled && !dialOutEnabled);
    const dialIn = state['features/invite'];
    const phoneNumber = dialIn && dialIn.numbers ? _getDefaultPhoneNumber(dialIn.numbers) : undefined;
    const { meeting, password } = state['features/base/conference'];

    const inviteUrl = getInviteURL(state);
    const liveStreamViewURL
        = currentLiveStreamingSession
            && currentLiveStreamingSession.liveStreamViewURL;
    const localParticipant = getLocalParticipant(state);
    const localParticipantName = localParticipant?.name;

    const inviteURL = _decodeRoomURI(inviteUrl);
    const { t } = ownProps;

    let invite = localParticipantName
        ? `${localParticipantName} is inviting you to a scheduled RemotePC Meeting.\n`
        : `You are invited to a scheduled RemotePC Meeting.\n`;

    if( meeting.name ) { invite += '\nTopic: ' + meeting.name + '\n' }

    invite += `\nJoin RemotePC Meeting\n${inviteURL}\n`;

    if(meeting.password) {
        invite += 'Use Meeting Password : '+(meeting.password || password)+'\n'
    }

    if (liveStreamViewURL) {
        const liveStream = t('info.inviteLiveStream', {
            url: liveStreamViewURL
        });

        invite = `${invite}\n${liveStream}`;
    }

    if (shouldDisplayDialIn(dialIn) || true) {
        const dial = `One tap mobile\n+18186990758,,${meeting.pin}# (US)\n`
                   + '\nOr Dial\n(US) ' + '+1-818-699-0758' + '\n' + 'Use Meeting Pin: ' + meeting.pin.toString().replace(/^(.{3})(.{3})(.*)$/, "$1 $2 $3") + '\n';
        const moreNumbers = `Find more numbers: ${getDialInfoPageURL(state)}`;

        invite = `${invite}\n${dial}\n${moreNumbers}`;
    }

    return {
        _dialIn: dialIn,
        _dialInVisible: isSharingEnabled(sharingFeatures.dialIn),
        _urlSharingVisible: isSharingEnabled(sharingFeatures.url),
        _emailSharingVisible: isSharingEnabled(sharingFeatures.email),
        _invitationText: invite,
        _inviteContactsVisible: interfaceConfig.ENABLE_DIAL_OUT && !hideInviteContacts,
        _inviteUrl: getInviteURL(state),
        _liveStreamViewURL: liveStreamViewURL,
        _phoneNumber: phoneNumber
        , _meeting: meeting, _password: password 
    };
}

/**
 * Maps dispatching of some action to React component props.
 *
 * @param {Function} dispatch - Redux action dispatcher.
 * @returns {Props}
 */
const mapDispatchToProps = {
    updateNumbers: () => updateDialInNumbers()
};

export default translate(
    connect(mapStateToProps, mapDispatchToProps)(MeetingContent)
);
